<template>
	<VBtn
		:id="uniqueId"
		class="amelipro-tile-btn text-none"
		:disabled="disabled"
		:elevation="1"
		height="auto"
		:href="href"
		:min-height="tileMinHeight"
		:ripple="false"
		:style="tileStyles"
		:to="to"
		:width="tileWidth"
		@blur="focus = false"
		@click="emitClickEvent()"
		@focus="focus = true"
		@mouseenter="hover = true"
		@mouseleave="hover = false"
	>
		<span class="d-block text-h5 text-center font-weight-semibold amelipro-tile-btn__content">
			{{ label }}

			<span
				v-if="labelBottom"
				class="d-block mt-4 text-h5 text-center font-weight-semibold amelipro-tile-btn__content--bottom"
			>
				{{ labelBottom }}
			</span>

			<span
				v-if="valid"
				class="d-sr-only"
			>
				&nbsp;validé
			</span>
		</span>
		<img
			alt=""
			class="mt-4 amelipro-tile-btn__img"
			:src="imgSrc"
			:style="imgStyles"
		>

		<AmeliproIcon
			v-if="valid"
			class="amelipro-tile-btn__icon"
			icon="check"
			icon-color="ap-turquoise darken-1"
			size="45px"
		/>
	</VBtn>
</template>

<script setup lang="ts">
	import { computed, ref } from 'vue';
	import AmeliproIcon from '../AmeliproIcon';
	import { IndexedObject } from '../../types';
	import { PropType } from 'vue/types/v3-component-props';
	import { RawLocation } from 'vue-router';
	import { VBtn } from 'vuetify/lib';
	import { convertToHex } from '../../functions/convertToHex';

	const props = defineProps({
		borderDash: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		href: {
			type: String,
			default: undefined,
		},
		imgMaxWidth: {
			type: String,
			default: undefined,
		},
		imgMinWidth: {
			type: String,
			default: undefined,
		},
		imgSrc: {
			type: String,
			required: true,
		},
		imgWidth: {
			type: String,
			default: '100px',
		},
		label: {
			type: String,
			required: true,
		},
		labelBottom: {
			type: String,
			default: undefined,
		},
		tileMinHeight: {
			type: String,
			default: undefined,
		},
		tilePadding: {
			type: String,
			default: '2rem 1.5rem',
		},
		tileWidth: {
			type: String,
			default: '100%',
		},
		to: {
			type: [Array, Object, String] as PropType<RawLocation>,
			default: undefined,
		},
		uniqueId: {
			type: String,
			default: undefined,
		},
		valid: {
			type: Boolean,
			default: false,
		},
	});

	const hover = ref(false);
	const focus = ref(false);

	const tileStyles = computed<IndexedObject>(() => {
		const btnStyles: IndexedObject = {
			backgroundColor: convertToHex('ap-white'),
			border: `2px solid ${convertToHex('ap-white')} !important`,
			color: convertToHex('ap-grey darken-1'),
			padding: props.tilePadding,
		};
		if (props.borderDash) {
			btnStyles.border = `2px dashed ${convertToHex('ap-blue lighten-1')} !important`;
		}
		if (hover.value && !props.disabled) {
			btnStyles.border = `2px solid ${convertToHex('ap-blue darken-1')} !important`;

			if (props.borderDash) {
				btnStyles.border = `2px dashed ${convertToHex('ap-blue darken-1')} !important`;
			}
		}
		if (props.disabled) {
			btnStyles.color = `${convertToHex('ap-grey darken-1')} !important`;
			btnStyles.backgroundColor = `${convertToHex('ap-grey lighten-2')} !important`;
			btnStyles.border = `2px solid ${convertToHex('ap-grey lighten-2')} !important`;
		}

		return btnStyles;
	});

	const imgStyles = computed<IndexedObject>(() => ({
		maxWidth: props.imgMaxWidth,
		minWidth: props.imgMinWidth,
		width: props.imgWidth,
	}));

	const emit = defineEmits(['click']);
	const emitClickEvent = (): void => {
		emit('click', props.uniqueId);
	};

</script>

<style lang="scss" scoped>
.amelipro-tile-btn {
	position: relative;
	display: flex;
	flex-direction: column;
	background-color: $ap-white;
	border-radius: 0.5rem;
	white-space: normal;
}

.v-btn {
	letter-spacing: unset;
	text-indent: unset;

	&.v-btn--active::before,
	&:focus::before,
	&:hover::before {
		content: unset !important;
		opacity: 0 !important;
	}

	&:focus {
		outline: 1px dotted $ap-grey-darken1;
	}

	& :deep(.v-btn__content) {
		position: unset;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		align-items: center;
		max-width: 100%;
	}
}

.amelipro-tile-btn__icon {
	position: absolute;
	bottom: 0;
	right: 10px;
}
</style>
